/**
 * <AssetPicker />
 * ---------------------------------------------------------------
 * Premium modal for browsing the Content Vault and picking one (or
 * many) assets by their permanent Content ID. Shared component —
 * used by JournalAdmin (cover selection) and later by Site Media,
 * Training Cards, etc.
 *
 * Props
 *   open              — controlled visibility
 *   onClose           — called when the user backs out
 *   onSelect(asset)   — called with the full asset row on pick.
 *                       For multi-select, `asset` is an array.
 *   multiple          — allow picking >1 asset
 *   defaultFilter     — { only_confirmed?: bool, virtue?: string, ... }
 *                       merged into every list call. `only_confirmed`
 *                       defaults to true so the picker never shows
 *                       raw un-analysed assets.
 *   title             — modal header override
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X, Search, Loader2, Check, CheckCircle2, Sparkles, FileText, ImageIcon,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { adminApi, API_BASE, readAdminToken } from "@/lib/adminApi";

const VIRTUES = ["Wisdom", "Justice", "Courage", "Temperance"];
const LANGUAGES = [
  { code: "",     label: "All" },
  { code: "en",   label: "EN" },
  { code: "nl",   label: "NL" },
  { code: "mixed",label: "Mixed" },
  { code: "other",label: "Other" },
];
const ASSET_TYPES = [
  { code: "",         label: "All" },
  { code: "image",    label: "Images" },
  { code: "document", label: "PDFs" },
];

function adminThumb(assetId) {
  const token = readAdminToken();
  return `${API_BASE}/api/admin/vault/file/${assetId}?variant=thumb&auth=${encodeURIComponent(token || "")}`;
}

export default function AssetPicker({
  open,
  onClose,
  onSelect,
  multiple = false,
  defaultFilter = { only_confirmed: true },
  title = "Choose from Content Vault",
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [virtues, setVirtues] = useState([]);   // ["Wisdom", ...]
  const [language, setLanguage] = useState("");
  const [assetType, setAssetType] = useState("");
  const [tag, setTag] = useState("");
  const [theme, setTheme] = useState("");
  const [emotion, setEmotion] = useState("");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);          // full asset row on right pane
  const [picked, setPicked] = useState([]);            // for multi mode
  const seq = useRef(0);

  // Debounce free-text search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const csv = (arr) => (arr || []).filter(Boolean).join(",");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const my = ++seq.current;
    try {
      const params = new URLSearchParams();
      params.set("limit", "60");
      // `only_confirmed` filters on the AI-enrichment lifecycle (`ai_status`),
      // NOT on the publish workflow status. It's ON by default because the
      // legacy picker was designed for curators browsing AI-enriched assets;
      // consumers who need "every published asset" (Site Media) pass
      // `defaultFilter={{ only_confirmed: false, status: "published" }}`.
      if (defaultFilter.only_confirmed !== false) params.set("only_confirmed", "true");
      if (defaultFilter.status) params.set("status", defaultFilter.status);
      if (debouncedQ) params.set("search", debouncedQ);
      if (virtues.length) params.set("virtue", csv(virtues));
      if (language) params.set("language", language);
      if (assetType) params.set("asset_type", assetType);
      if (tag) params.set("tag", tag);
      if (theme) params.set("theme", theme);
      if (emotion) params.set("emotion", emotion);
      const token = readAdminToken();
      const res = await fetch(`${API_BASE}/api/admin/vault/assets?${params.toString()}`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (my !== seq.current) return; // stale
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (my === seq.current) setErr(e.message || String(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [debouncedQ, virtues, language, assetType, tag, theme, emotion, defaultFilter.only_confirmed, defaultFilter.status]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      // Reset transient state on close so the next open starts fresh.
      setDetail(null);
      setPicked([]);
    }
  }, [open]);

  const toggleVirtue = (v) => setVirtues((cur) => cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]);
  const isPicked = (a) => picked.find((x) => x.id === a.id);

  const confirm = () => {
    if (multiple) {
      onSelect?.(picked);
    } else if (detail) {
      onSelect?.(detail);
    }
    onClose?.();
  };
  const pickSingle = (a) => {
    if (multiple) {
      setPicked((cur) => (isPicked(a) ? cur.filter(x => x.id !== a.id) : [...cur, a]));
    } else {
      setDetail(a);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="relative max-w-6xl w-full max-h-[92vh] border border-gold/40 bg-[#0A0A0A] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="asset-picker"
      >
        <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-gold">
            <Sparkles size={16} strokeWidth={1.5} />
            <h3 className="font-serif text-xl">{title}</h3>
            <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-bone/45">
              {total} {total === 1 ? "asset" : "assets"}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="asset-picker-close"
            className="text-bone/60 hover:text-bone"
          ><X size={16} /></button>
        </header>

        <div className="flex-1 min-h-0 grid grid-cols-[240px_1fr_260px] gap-0">
          {/* --- Filter rail --- */}
          <aside className="border-r border-white/10 p-4 space-y-4 overflow-y-auto text-[11px]" data-testid="asset-picker-filters">
            <div>
              <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-2">Language</p>
              <div className="flex flex-wrap gap-1">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setLanguage(l.code)}
                    data-testid={`ap-lang-${l.code || "all"}`}
                    className={`px-2 py-1 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                      language === l.code ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-bone/55 hover:text-bone"
                    }`}
                  >{l.label}</button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-2">Virtues</p>
              <div className="flex flex-wrap gap-1">
                {VIRTUES.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleVirtue(v)}
                    data-testid={`ap-virtue-${v.toLowerCase()}`}
                    className={`px-2 py-1 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                      virtues.includes(v) ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-bone/55 hover:text-bone"
                    }`}
                  >{v}</button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-2">Type</p>
              <div className="flex flex-wrap gap-1">
                {ASSET_TYPES.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    onClick={() => setAssetType(t.code)}
                    data-testid={`ap-type-${t.code || "all"}`}
                    className={`px-2 py-1 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                      assetType === t.code ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-bone/55 hover:text-bone"
                    }`}
                  >{t.label}</button>
                ))}
              </div>
            </div>

            <FilterInput label="Tag" value={tag} setValue={setTag} testid="ap-tag" />
            <FilterInput label="Theme" value={theme} setValue={setTheme} testid="ap-theme" />
            <FilterInput label="Emotion" value={emotion} setValue={setEmotion} testid="ap-emotion" />

            <button
              type="button"
              onClick={() => {
                setVirtues([]); setLanguage(""); setAssetType("");
                setTag(""); setTheme(""); setEmotion(""); setQ("");
              }}
              data-testid="ap-clear-filters"
              className="w-full text-[10px] tracking-[0.24em] uppercase text-bone/50 border border-white/10 px-2 py-1.5 hover:text-bone hover:border-white/25"
            >
              Clear all
            </button>
          </aside>

          {/* --- Results grid --- */}
          <section className="min-w-0 flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 flex-shrink-0">
              <Search size={14} className="text-bone/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, description, OCR, Content ID…"
                data-testid="ap-search"
                className="flex-1 bg-transparent text-[13px] text-bone placeholder:text-bone/30 focus:outline-none"
              />
              {loading && <Loader2 size={14} className="animate-spin text-gold" />}
            </div>

            <div className="flex-1 overflow-y-auto p-4" data-testid="ap-results">
              {err && <p className="text-[11px] text-red-400 mb-3">{err}</p>}
              {items.length === 0 && !loading ? (
                <div className="text-center py-16 text-bone/40 text-[11px] tracking-[0.24em] uppercase">
                  No assets match these filters
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {items.map((a) => (
                    <AssetCard
                      key={a.id}
                      asset={a}
                      picked={multiple ? !!isPicked(a) : detail?.id === a.id}
                      onClick={() => pickSingle(a)}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* --- Detail panel --- */}
          <aside className="border-l border-white/10 p-4 overflow-y-auto" data-testid="asset-picker-detail">
            {detail || (multiple && picked.length > 0) ? (
              multiple ? (
                <div className="space-y-2">
                  <p className="text-[10px] tracking-[0.24em] uppercase text-gold">
                    {picked.length} picked
                  </p>
                  <ul className="text-[11px] text-bone/70 space-y-1">
                    {picked.map(p => (
                      <li key={p.id} className="flex items-center gap-2">
                        <CheckCircle2 size={11} className="text-emerald-400" />
                        <span className="text-gold font-mono">{p.content_id}</span>
                        <span className="truncate">{p.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <DetailPanel asset={detail} />
              )
            ) : (
              <p className="text-[11px] text-bone/40 italic">Select an asset to preview.</p>
            )}
          </aside>
        </div>

        <footer className="px-6 py-3 border-t border-white/10 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            data-testid="asset-picker-cancel"
            className="text-[10px] tracking-[0.24em] uppercase text-bone/60 border border-white/15 px-3 py-1.5 hover:text-bone"
          >Cancel</button>
          <button
            type="button"
            onClick={confirm}
            disabled={multiple ? picked.length === 0 : !detail}
            data-testid="asset-picker-confirm"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-4 py-1.5 hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check size={11} /> {multiple ? `Use ${picked.length}` : "Use this asset"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function FilterInput({ label, value, setValue, testid }) {
  return (
    <div>
      <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-1">{label}</p>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={testid}
        className="w-full bg-black border border-white/15 text-bone px-2 py-1 text-[11px] focus:outline-none focus:border-gold"
      />
    </div>
  );
}

function AssetCard({ asset, picked, onClick }) {
  const ai = asset.ai_analysis || {};
  const isDoc = asset.asset_type === "document";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`ap-card-${asset.content_id}`}
      className={`text-left border transition-all ${
        picked ? "border-gold/80 ring-1 ring-gold/40" : "border-white/10 hover:border-white/25"
      } bg-black`}
    >
      <div className="aspect-square bg-white/[0.02] relative overflow-hidden">
        <img
          src={adminThumb(asset.id)}
          alt={asset.title || asset.content_id}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
        />
        {isDoc && (
          <span className="absolute top-1.5 left-1.5 bg-black/70 border border-white/15 px-1.5 py-0.5 flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase text-bone/70">
            <FileText size={9} /> PDF
          </span>
        )}
        {asset.ai_status === "confirmed" && (
          <span className="absolute top-1.5 right-1.5 bg-emerald-400/15 border border-emerald-400/40 px-1 py-0.5 text-[9px] text-emerald-300">AI</span>
        )}
        {picked && (
          <span className="absolute bottom-1.5 right-1.5 bg-gold/90 text-black rounded-full p-1">
            <Check size={10} strokeWidth={3} />
          </span>
        )}
      </div>
      <div className="px-2 py-1.5 space-y-0.5">
        <p className="text-[10px] text-gold font-mono">{asset.content_id}</p>
        <p className="text-[11px] text-bone/85 truncate">{asset.title || "(untitled)"}</p>
        {(ai.virtues && ai.virtues.length > 0) && (
          <p className="text-[9px] tracking-[0.2em] uppercase text-bone/40 truncate">{ai.virtues.join(" · ")}</p>
        )}
      </div>
    </button>
  );
}

function DetailPanel({ asset }) {
  if (!asset) return null;
  const ai = asset.ai_analysis || {};
  return (
    <div className="space-y-3">
      <img
        src={adminThumb(asset.id)}
        alt={asset.title}
        className="w-full aspect-square object-cover border border-white/10"
      />
      <div>
        <p className="text-[10px] text-gold font-mono">{asset.content_id}</p>
        <h4 className="text-[13px] text-bone leading-tight mt-0.5">{asset.title || "(untitled)"}</h4>
      </div>
      {ai.description_en && (
        <p className="text-[11px] text-bone/70 leading-relaxed">{ai.description_en}</p>
      )}
      {ai.virtues && ai.virtues.length > 0 && (
        <div>
          <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-1">Virtues</p>
          <div className="flex flex-wrap gap-1">
            {ai.virtues.map(v => (
              <span key={v} className="border border-gold/40 bg-gold/5 text-gold px-1.5 py-0.5 text-[10px] tracking-[0.18em] uppercase">{v}</span>
            ))}
          </div>
        </div>
      )}
      {ai.tags && ai.tags.length > 0 && (
        <div>
          <p className="text-[9px] tracking-[0.24em] uppercase text-bone/45 mb-1">Tags</p>
          <div className="flex flex-wrap gap-1">
            {ai.tags.slice(0, 8).map(t => (
              <span key={t} className="text-[10px] text-bone/60 bg-white/[0.04] px-1.5 py-0.5">{t}</span>
            ))}
          </div>
        </div>
      )}
      <dl className="text-[11px] text-bone/60 space-y-0.5 pt-1 border-t border-white/10">
        <div className="flex justify-between"><dt>Type</dt><dd>{asset.file_type}</dd></div>
        <div className="flex justify-between"><dt>Size</dt><dd>{(asset.file_size / 1024).toFixed(1)} KB</dd></div>
        {asset.width && <div className="flex justify-between"><dt>Dimensions</dt><dd>{asset.width} × {asset.height}</dd></div>}
        {ai.detected_language && <div className="flex justify-between"><dt>Language</dt><dd className="uppercase">{ai.detected_language}</dd></div>}
      </dl>
    </div>
  );
}
